/**
 * Welding Repair Analysis Services - API service layer for welding repair workflow operations
 * Handles repair workflow management, approvals, project tracking, and analysis operations
 */
import axios from "axios";

// Dynamic API URL configuration based on environment
let REPAIR_API_URL;
if (process.env.REACT_APP_NODE_ENV === "local") {
  REPAIR_API_URL = process.env.REACT_APP_WELD_REPAIR_API_URL;
} else {
  REPAIR_API_URL = process.env.REACT_APP_API_URL + "weldRepair/weldRepairAnalysis";
}

/**
 * Get approved and pending repair workflows for user - ACTIVELY USED
 * Purpose: Retrieve repair workflows filtered by approval status for specific user
 * Used in: Repair workflow components for displaying user-specific workflows
 * @param {Object} userObj - User object containing user details for filtering
 * @returns {Object} Approved and pending repair workflow records
 */
export async function getApprovedPendingRepairWorkflow(userObj) {
  let url = `${REPAIR_API_URL}/getApprovedPendingRepairWorkflow`;
  const response = await axios
    .post(url, userObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Add welding repair analysis record - ACTIVELY USED
 * Purpose: Create new welding repair analysis with specified action
 * Used in: WeldingRepairAnalysisEntry.jsx for creating and updating repair analysis records
 * @param {Object} weldingRepairObj - Welding repair analysis data
 * @param {string} action - Action type for the repair analysis
 * @returns {Object} Creation response
 */
export const addWeldingRepairAnalysis = async (weldingRepairObj, action) => {
  let url = `${REPAIR_API_URL}/addWeldingRepairAnalysis`;
  let dataObj = { weldingRepairObj: weldingRepairObj, action: action };
  const response = await axios
    .post(url, dataObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};
/**
 * Add welding repair roles - ACTIVELY USED
 * Purpose: Create welding repair role assignments
 * Used in: WeldingRepairAnalysisEntry.jsx for setting up role-based workflow assignments
 * @param {Object} weldingRepairRoleObj - Welding repair role data
 * @returns {Object} Creation response
 */
export const addWeldingRepairRoles = async (weldingRepairRoleObj) => {
  let url = `${REPAIR_API_URL}/weldrepairole`;
  const response = await axios
    .post(url, weldingRepairRoleObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};


/**
 * Sort array by property in descending order - ACTIVELY USED
 * Purpose: Utility function to create a comparator for sorting arrays by property in descending order
 * Used in: WeldingRepairAnalysisEntry.jsx for sorting revision numbers in descending order
 * @param {string} property - Property name to sort by
 * @returns {Function} Comparator function for Array.sort()
 */
export function sortByPropertyInDescending(property) {
  return function (a, b) {
    if (a[property] < b[property]) return 1;
    else if (a[property] > b[property]) return -1;
    return 0;
  };
}

/**
 * Get closure repair workflow data - ACTIVELY USED
 * Purpose: Retrieve repair workflow closure information
 * Used in: ClosureRemarksWeldingRepair.jsx and WeldingRepairAnalysisEntry.jsx for workflow closure tracking
 * @param {Object} tmpObj - Parameters for closure workflow retrieval
 * @returns {Object} Closure repair workflow data
 */
export async function getClosureRepairWorkflow(tmpObj) {
  let url = `${REPAIR_API_URL}/getClosureRepairWorkflow`;
  const response = await axios
    .post(url, tmpObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/* export async function updateClosureRepairWorkflow(data) {

  let url = `${REPAIR_API_URL}/updateClosureRepairWorkflow`;
  const response = await axios
    .post(url, data)

    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
} */

/**
 * Get project request count - ACTIVELY USED
 * Purpose: Retrieve count of project requests for dashboard/reporting
 * Used in: Dashboard components for displaying project request statistics
 * @returns {Object} Project request count data
 */
export async function getProjectReqCount() {
  let url = `${REPAIR_API_URL}/projectreqcount`;
  const response = await axios
    .get(url)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Get total approved count by year - ACTIVELY USED
 * Purpose: Retrieve count of approved items for a specific year for reporting
 * Used in: WeldingRepairAnalysis.jsx for generating monthly repair charts and analytics
 * @param {string|number} Year - Year to get approved count for
 * @returns {Object} Total approved count for the specified year
 */
export async function getallApprovedCount(Year) {
  let keyObj = {
    year: Year,
  };
  let url = `${REPAIR_API_URL}/totalApprovedCount`;
  const response = await axios
    .post(url, keyObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Get project request count by project - ACTIVELY USED
 * Purpose: Retrieve request count data filtered by specific project
 * Used in: WeldingRepairAnalysisEntry.jsx for generating sequence numbers for new repair reports
 * @param {Object} projObj - Project object containing project details for filtering
 * @returns {Object} Project-wise request count data
 */
export async function getProjectReqCountProjectWise(projObj) {
  let url = `${REPAIR_API_URL}/getprojectreqcountprojectwise`;
  const response = await axios
    .post(url, projObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

